<script setup>
import moment from 'moment-timezone'
import {setMetaData} from '@/plugins/misc'
import {useUsersStore, useReservationsStore, useUserGroupsStore, useRestaurantsStore} from '@/stores'
import {onBeforeMount, onBeforeUnmount, ref, computed, onMounted, watch, onUnmounted} from 'vue'
import {useRoute, useRouter} from "vue-router"
import noResEmptyState from '@/assets/images/ui/no_reservations_empty_state.jpg'
import googleAttribution from '@/assets/images/ui/google_on_white.png'
import { useTitle  } from '@vueuse/core'
import Testimonials from "@core/components/Testimonials.vue"
import { isUserLoggedIn } from '@/router/utils'

const usersStore = useUsersStore()
let myUser = null
let loginDialogMode = null
let users = [];
({myUser, users, loginDialogMode} = storeToRefs(usersStore))

const userGroupsStore = useUserGroupsStore()

const reservationsStore = useReservationsStore()
let myReservations = null
let fetching = null
let editingReservation = null;
({myReservations, editingReservation, fetching} = storeToRefs(reservationsStore))

const restaurantsStore = useRestaurantsStore()

//doing this on mainMenu now
// restaurantsStore.getRestaurants()
let restaurants = null;
({restaurants} = storeToRefs(restaurantsStore))

const restaurantToShow = ref(null)

const route = useRoute()
const router = useRouter()

// The profile tab a ?tab= link opens, when that tab is there to open; otherwise the default.
// ?tab=MemberHighlights: the team's "posted publicly" notification. ?tab=Events: Group/Private,
// shown only for a restaurant that takes inquiries and has spaces on record (RM AI's link to a
// restaurant's spaces, 10/22).
const startAtTab = computed(() => {
  const tab = route.query.tab
  if (tab === 'MemberHighlights') return 'MemberHighlights'
  if (tab === 'Events' && !!restaurantToShow.value?.acceptingReferrals && restaurantToShow.value?.eventSpaces?.length > 0) return 'Events'

  return undefined
})

// Local state
const isLoading = ref(true)
const isInitialized = ref(false)

// Computed properties
const myUserComputed = computed(() => usersStore.myUser)
const restaurantsComputed = computed(() => restaurantsStore.restaurants)
const restaurantSlug = computed(() => {
  const param = route.params.all
  
  return Array.isArray(param) ? param.join('/') : param
})

// Initialize the page data
const initializePage = async () => {
  console.log('Initializing restaurant page data for:', restaurantSlug.value)
  
  // Clear previous restaurant data and show loading
  restaurantToShow.value = null
  isLoading.value = true

  try {
    // Ensure user data is loaded if logged in
    if (isUserLoggedIn() && !myUserComputed.value) {
      await usersStore.initializeUser()
    }

    // Fetch the specific restaurant by slug
    if (restaurantSlug.value) {
      const restaurant = await restaurantsStore.getRestaurantByProfileId(restaurantSlug.value)
      
      // Check if restaurant is unlisted - if so, don't show it
      if (restaurant && restaurant.status === 'Unlisted') {
        console.log('Restaurant is unlisted, access denied')
        restaurantToShow.value = null
      } else {
        restaurantToShow.value = restaurant

        // Set page metadata
        if (restaurant) {
          setMetaData(
            'RM - ' + restaurant.name,
            `${(restaurant.status == "Preferred" ? 'Preferred Restaurant' : '')} Restaurant - ${restaurant.market}`,
            restaurant.imageUrl || 'https://reserverm.com/logo.png',
            `https://reserverm.com/r/${restaurantSlug.value}`
          )
        }
      }
    }

    isInitialized.value = true
  } catch (error) {
    console.error('Error initializing restaurant page:', error)
  } finally {
    isLoading.value = false
  }
}

// Watch for route changes within the component
watch(() => route.params.all, (newSlug, oldSlug) => {
  if (newSlug && newSlug !== oldSlug) {
    console.log('Restaurant slug changed:', newSlug)
    initializePage()
  }
})

// Initialize on component mount
onMounted(() => {
  console.log('Restaurant page mounted')
  initializePage()
})

const jwt = JSON.parse(localStorage.getItem('jwt') || 'null')
</script>

<template>
  <div>
    <!-- Back to Restaurant Admin Button -->
    <!--
      <div v-if="myUser?.profileId && myUser?.userLevel !== 'dummy'" class="back-button-container">
      <RouterLink :to="`/m/${myUser.profileId}`" class="back-button d-flex align-center cursor-pointer">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="m15 18-6-6 6-6"></path>
      </svg>
      <span class="ml-1">Back to Restaurant Admin</span>
      </RouterLink>
      </div> 
    -->

    <!-- Add a loading state when the page is initializing -->
    <div
      v-if="isLoading"
      class="loading-container"
    >
      <div class="loading-logo">
        <img
          src="/logo_no_border.png"
          height="100%"
          alt="Logo"
        >
      </div>
      <div class="loading">
        <div class="effect-1 effects" />
        <div class="effect-2 effects" />
        <div class="effect-3 effects" />
      </div>
    </div>

    <div v-else-if="restaurantToShow">
      <div class="mb-4">
        <!-- ?tab=MemberHighlights / ?tab=Events open that tab when it is there (see startAtTab). -->
        <RestaurantProfile
          :key="restaurantToShow._id || restaurantToShow.id"
          :restaurant="restaurantToShow"
          :start-at-tab="startAtTab"
        />
      </div>
    </div>
    <div
      v-else
      class="not-found-container"
    >
      <h2>Restaurant Not Found</h2>
      <p>The restaurant you're looking for could not be found.</p>
      <VBtn
        to="/discover"
        color="primary"
      >
        Discover Restaurants
      </VBtn>
    </div>

    <!-- Footer -->
    <div class="d-flex flex-column align-center justify-center">
      <p
        class="rm-footer-message text-center text-wrap mt-4 pa-4"
        style="white-space: normal; max-width: 460px;"
      >
        Network of Artists, Athletes, CEOs, Investors &amp; Restaurateurs who highly value where they dine
      </p>
      <div class="d-flex flex-column align-center justify-center">
        <p class="text-center text-caption">
          <RouterLink
            class="text-primary"
            :to="{ path: '/members' }"
          >
            Members
          </RouterLink>&nbsp;&nbsp;|&nbsp;
          <RouterLink
            class="text-primary"
            :to="{ path: '/restaurants' }"
          >
            Restaurants
          </RouterLink>
        </p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.back-button-container {
  padding: var(--ds-space-2) var(--ds-space-3);
}

.back-button {
  padding: var(--ds-space-2) var(--ds-space-3);
  color: var(--ds-text-strong);
  text-decoration: none;
  font-size: var(--ds-text-base);
  transition: background-color var(--ds-duration-normal);
  border-radius: var(--ds-radius-xs);
  display: inline-flex;
}

.back-button:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.back-button svg {
  color: var(--ds-text-muted);
}

.rm-footer-message {
  font-size: var(--ds-text-xs);
}

.tab-height {
  height: 60px !important;
}

.upcoming-empty-state {
  min-height: 36vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.action-buttons {
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  z-index: var(--ds-z-dropdown);
  background: var(--ds-surface-card);
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  border-top: thin solid var(--ds-border-default);
  padding: 1rem;
  box-shadow: 0px -3px 6px rgba(0,0,0,0.025);
}
@media screen and (min-width: 670px) {
  .profile-actions-edit-button {
    display: none;
  }
}

.reservation-card {
  overflow: hidden;
  border-radius: var(--ds-radius-sm);
  border: thin solid var(--ds-border-light);
  margin-bottom: 0.5rem;
  width: 100%;
}

.reservation-card-title {
  font-size: 1rem;
  color: var(--ds-text-muted);
}

@media screen and (min-width: 430px) {
  .reservation-status {
    position: absolute;
    top: 1rem;
    right: 1rem;
    z-index: 1;
    margin: 0;
  }
}

.reservation-card:first-child {
  margin-top: 0.5rem;
}

.reservation-card-content {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
}

@media screen and (min-width: 430px) {
  .reservation-card-content {
    flex-direction: row;
    padding: 1rem;
  }
}

.restaurant-image-holder {
  width: 100%;
  aspect-ratio: 16/7;
  background: var(--ds-surface-inset);
  border-radius: 0;
  overflow: hidden;
}

.no-reservations-empty-state {
  width: 100%;
  max-width: 200px;
  opacity: 0.8;
}

@media screen and (min-width: 430px) {
  .restaurant-image-holder {
    width: 8rem;
    height: 8rem;
    aspect-ratio: 1;
    border-radius: var(--ds-radius-sm);
  }
}

.restaurant-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.header-new-reservation-button-sticky-holder {
  overflow: visible;
  position: sticky;
  z-index: 10;
  top: 5.5rem;
}

.google-attribution {
  width: fit-content;
  margin-bottom: 1rem;
  margin-left: 0.4rem;
}

.reservations-loading-spinner {
  position: absolute;
  right: 5px;
  top: 20px;
}

.action-buttons {
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  z-index: var(--ds-z-dropdown);
  background: var(--ds-surface-card);
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  border-top: thin solid var(--ds-border-default);
  padding: 1rem;
  box-shadow: 0px -3px 6px rgba(0,0,0,0.025);
}

.declined {
  position: relative;
}

.declined::after {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  right: 0;
  border-top: 1px solid red; /* Strikethrough line color */
  transform: translateY(-50%);
}
.declines {
  color: red;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

.not-found-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  text-align: center;
}
</style>

<route lang="yaml">
meta:
  layout: website
  allowLoggedInOnly: false
</route>
